import { c as _c } from "react/compiler-runtime";
import Button, { type ButtonProps } from '@precis-digital/kurama/Button';
import CancelIcon from '@precis-digital/kurama/icons/CancelIcon';
import CheckIcon from '@precis-digital/kurama/icons/CheckIcon';
import { type ReactElement, useRef, useState } from 'react';

/** A yes/no answer the user gives a copilot tool call before it takes effect. */
export type CopilotHitlDecision = 'approved' | 'rejected';

/**
 * Tracks the user's answer to a HITL tool call.
 *
 * Prefers the answer given in this session, falling back to the decision already
 * recorded in the tool result, so a card rendered from chat history stays
 * resolved. Refs mirror pending/decision so `startDecision` rejects a second
 * click in the same turn, before React re-renders and disables the buttons.
 */
export const useCopilotHitlDecision = resultStatus => {
  const $ = _c(7);
  const [localDecision, setLocalDecision] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const localDecisionRef = useRef(null);
  const isPendingRef = useRef(false);
  const decision = localDecision ?? (resultStatus === "approved" || resultStatus === "rejected" ? resultStatus : null);
  let t0;
  if ($[0] !== resultStatus) {
    t0 = (next, options) => {
      const resolvedDecision = localDecisionRef.current ?? (resultStatus === "approved" || resultStatus === "rejected" ? resultStatus : null);
      if (resolvedDecision != null || isPendingRef.current) {
        return false;
      }
      if (options?.isAsync === true) {
        isPendingRef.current = true;
        setIsPending(true);
      } else {
        localDecisionRef.current = next;
        setLocalDecision(next);
      }
      return true;
    };
    $[0] = resultStatus;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const startDecision = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = next_0 => {
      isPendingRef.current = false;
      setIsPending(false);
      if (next_0 != null) {
        localDecisionRef.current = next_0;
        setLocalDecision(next_0);
      }
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const finishDecision = t1;
  let t2;
  if ($[3] !== decision || $[4] !== isPending || $[5] !== startDecision) {
    t2 = {
      decision,
      isPending,
      startDecision,
      finishDecision
    };
    $[3] = decision;
    $[4] = isPending;
    $[5] = startDecision;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export interface HitlDecisionActionsProps {
  onDecision: (decision: CopilotHitlDecision) => void;
  approveLabel: string;
  rejectLabel: string;
  /** Styles approve as destructive and reject as neutral, for irreversible actions. */
  isDestructive?: boolean;
  disabled?: boolean;
  /** Disables only Confirm, e.g. until the user has selected which item to act on. */
  approveDisabled?: boolean;
}

/**
 * The Approve/Reject button pair every HITL tool call card shows, so the
 * ordering, icons, and variants stay identical across cards.
 * Returns the array `ActionCard`'s `actions` expects.
 */
export const buildHitlDecisionActions = ({
  onDecision,
  approveLabel,
  rejectLabel,
  isDestructive = false,
  disabled = false,
  approveDisabled = false
}: HitlDecisionActionsProps): Array<ReactElement<ButtonProps>> => [<Button key="reject" variant={isDestructive ? 'tonal' : 'semantic-destructive'} rightIcon={<CancelIcon />} disabled={disabled} onClick={() => {
  onDecision('rejected');
}}>
    {rejectLabel}
  </Button>, <Button key="approve" variant={isDestructive ? 'semantic-destructive' : 'semantic-promo'} rightIcon={<CheckIcon />} disabled={disabled || approveDisabled} onClick={() => {
  onDecision('approved');
}}>
    {approveLabel}
  </Button>];